import { useState, useEffect } from 'react';
import {
  getSensors,
  createSensor,
  deleteSensor,
  updateSensor,
  approveUser,
  type Sensor
} from '../lib/admin';
import { getUser } from '../lib/auth';
import { Users, Plus, Trash2, Mail, CheckCircle, XCircle, Clock, Edit } from 'lucide-react';
import { useToast, useConfirm } from './ui';

export default function SensorManagement() {
  const currentUser = getUser();
  const toast = useToast();
  const { confirm } = useConfirm();

  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSensor, setEditingSensor] = useState<Sensor | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    full_name: ''
  });

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    email: '',
    full_name: '',
    status: 'active'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Systemadmin sees all sensors without filtering
      // Admin sees sensors for their study association
      // Member sees only sensors for their own member organization
      const filters: any = {};
      if (currentUser.role === 'member') {
        // Member: only see sensors for their specific organization
        filters.member_id = currentUser.member_id;
      } else if (currentUser.role === 'admin') {
        // Admin: see sensors for their study association
        filters.study_association_id = currentUser.study_association_id;
      }
      // systemadmin: no filter (sees all)

      const sensorsData = await getSensors(filters);

      setSensors(sensorsData);

    } catch (err: any) {
      setError(err.message || 'Kunne ikke laste data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.full_name) {
      toast.showWarning('E-post og fullt navn er påkrevd');
      return;
    }

    if (!currentUser.member_id) {
      toast.showWarning('Du må være tilknyttet en medlemsorganisasjon for å opprette sensorer');
      return;
    }

    try {
      setSaving(true);
      await createSensor({
        email: formData.email,
        full_name: formData.full_name,
        member_id: currentUser.member_id,
        study_association_id: currentUser.study_association_id
      });

      toast.showSuccess(`Sensor opprettet! En e-post med lenke for å sette passord er sendt til ${formData.email}.`);

      setShowCreateForm(false);
      resetForm();
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved opprettelse: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sensor: Sensor) => {
    // Hybrid-brukere (medlem/medlem-admin + sensor-flagg): fjern KUN sensor-flagget,
    // ikke deaktiver hele brukeren. Backend håndhever samme regel — denne dialogen
    // gjør intensjonen tydelig for admin.
    const isHybrid = !!sensor.is_hybrid;
    const primaryRole = sensor.is_member_admin ? 'medlem-administrator' : 'medlem';

    const confirmed = await confirm({
      title: isHybrid ? 'Fjern sensor-rolle' : 'Deaktiver sensor',
      message: isHybrid
        ? `${sensor.full_name} har også rollen ${primaryRole} og skal beholde den. Denne handlingen fjerner KUN sensor-tilgangen. Brukeren kan fortsatt logge inn og bruke sin ${primaryRole}-rolle som normalt.\n\nAlle sensurdata brukeren har lagt inn tidligere bevares.`
        : `Er du sikker på at du vil deaktivere ${sensor.full_name}?\n\nAlle sensorens graderingsdata vil beholdes, men tilgangen til systemet fjernes.`,
      confirmLabel: isHybrid ? 'Fjern sensor-rolle' : 'Deaktiver',
      cancelLabel: 'Avbryt',
      variant: 'warning'
    });

    if (!confirmed) return;

    try {
      const result: any = await deleteSensor(sensor.id);
      toast.showSuccess(
        isHybrid
          ? `Sensor-tilgangen til ${sensor.full_name} er fjernet. Brukeren beholder ${primaryRole}-rollen.`
          : `${sensor.full_name} er deaktivert`
      );
      // Unngå unused warning for result
      void result;
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved deaktivering: ${err.message}`);
    }
  };

  const handleApprove = async (sensorId: string) => {
    const confirmed = await confirm({
      title: 'Godkjenn sensor',
      message: 'Er du sikker på at du vil godkjenne denne sensoren?',
      confirmLabel: 'Godkjenn',
      cancelLabel: 'Avbryt',
      variant: 'default'
    });

    if (!confirmed) return;

    try {
      await approveUser(currentUser.id, sensorId);
      toast.showSuccess('Sensor godkjent! Sensoren kan nå logge inn.');
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved godkjenning: ${err.message}`);
    }
  };

  const handleEdit = (sensor: Sensor) => {
    setEditingSensor(sensor);
    setEditFormData({
      email: sensor.email,
      full_name: sensor.full_name,
      status: sensor.status
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingSensor) return;

    try {
      setSaving(true);
      await updateSensor(editingSensor.id, {
        email: editFormData.email,
        full_name: editFormData.full_name,
        status: editFormData.status
      });

      toast.showSuccess('Sensor oppdatert!');
      setShowEditModal(false);
      setEditingSensor(null);
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved oppdatering: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B5651D] mx-auto mb-4"></div>
          <p className="text-gray-600">Laster sensorer...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Sensorer
            </h1>
            <p className="text-gray-600">
              Administrer sensorer tilknyttet din organisasjon
            </p>
          </div>
          <button
            onClick={() => {
              setShowCreateForm(true);
              resetForm();
            }}
            className="flex items-center space-x-2 bg-[#B5651D] text-white px-4 py-2 rounded-lg hover:bg-[#8B4513] transition"
          >
            <Plus className="w-5 h-5" />
            <span>Inviter Sensor</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Inviter Ny Sensor
          </h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fullt Navn *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-post *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>
            </div>

            <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-3 text-sm text-kf-blue-800">
              <p className="font-medium mb-1">Hva skjer etter invitasjon:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Sensor mottar e-post med lenke for å sette passord</li>
                <li>Sensoren aktiveres automatisk (ingen godkjenning nødvendig)</li>
                <li>Lenken er gyldig i 1 time</li>
                <li>Etter å ha satt passord kan sensoren logge inn</li>
              </ul>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#B5651D] text-white py-2 rounded-lg hover:bg-[#8B4513] transition disabled:opacity-50"
              >
                {saving ? 'Oppretter...' : 'Inviter Sensor'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Avbryt
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sensors List */}
      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sensor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  E-post
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tildelte Kurs
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Opprettet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sensors.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>Ingen sensorer funnet for din organisasjon</p>
                    <p className="text-sm mt-1">Klikk "Inviter Sensor" for å legge til en ny sensor</p>
                  </td>
                </tr>
              ) : (
                sensors.map((sensor) => (
                  <tr key={sensor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        {sensor.full_name}
                        {sensor.is_hybrid && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200"
                            title="Denne brukeren har også en primærrolle (medlem/medlem-admin). Endringer av e-post og status via sensor-fanen er sperret for å beskytte innloggingen."
                          >
                            + {sensor.is_member_admin ? 'Medlem-admin' : 'Medlem'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-900">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                        {sensor.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        sensor.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : sensor.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {sensor.status === 'active' ? (
                          <>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Aktiv
                          </>
                        ) : sensor.status === 'pending' ? (
                          <>
                            <Clock className="w-3 h-3 mr-1" />
                            Avventer Godkjenning
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3 h-3 mr-1" />
                            Inaktiv
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {sensor.assigned_courses_count || 0} kurs
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(sensor.created_at).toLocaleDateString('nb-NO')}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center space-x-2">
                        {sensor.status === 'pending' && (
                          <button
                            onClick={() => handleApprove(sensor.id)}
                            className="text-green-600 hover:text-green-800 transition"
                            title="Godkjenn sensor"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(sensor)}
                          className="text-kf-blue-600 hover:text-kf-blue-800 transition"
                          title="Rediger sensor"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        {sensor.status === 'active' && (
                          <button
                            onClick={() => handleDelete(sensor)}
                            className="text-red-600 hover:text-red-800 transition"
                            title="Deaktiver sensor"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      {sensors.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Totalt Sensorer</p>
                <p className="text-2xl font-bold text-gray-900">{sensors.length}</p>
              </div>
              <Users className="w-8 h-8 text-[#B5651D]" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Aktive Sensorer</p>
                <p className="text-2xl font-bold text-green-600">
                  {sensors.filter(s => s.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avventer Godkjenning</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {sensors.filter(s => s.status === 'pending').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingSensor && (() => {
        const isHybrid = !!editingSensor.is_hybrid;
        const primaryRole = editingSensor.is_member_admin ? 'medlem-administrator' : 'medlem';
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {isHybrid ? 'Sensor-info (skrivebeskyttet)' : 'Rediger Sensor'}
              </h2>

              {isHybrid && (
                <div className="mb-4 rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm text-purple-900">
                  <div className="font-semibold mb-1">
                    Denne brukeren har rollen {primaryRole}
                  </div>
                  <p className="mb-2">
                    Sensor-fanen er kun for redigering av rene sensor-brukere. Fordi denne
                    personen også er {primaryRole}, deles alle kontofelt (navn, e-post, status)
                    med den rollen — de kan derfor ikke endres her.
                  </p>
                  <p className="mb-2">
                    <span className="font-medium">For å endre kontoinfo:</span>{' '}
                    gå til <span className="font-medium">Min organisasjon</span> →{' '}
                    <span className="font-medium">Brukere</span>.
                  </p>
                  <p>
                    <span className="font-medium">For å fjerne sensor-tilgangen</span> (men beholde
                    {' '}{primaryRole}-rollen): lukk denne dialogen og bruk «Fjern sensor-rolle»-knappen
                    i sensor-listen.
                  </p>
                </div>
              )}

              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fullt Navn {!isHybrid && '*'}
                  </label>
                  <input
                    type="text"
                    value={editFormData.full_name}
                    onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                    required={!isHybrid}
                    disabled={isHybrid}
                    className={`w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D] ${
                      isHybrid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-post {!isHybrid && '*'}
                  </label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    required={!isHybrid}
                    disabled={isHybrid}
                    className={`w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D] ${
                      isHybrid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                    disabled={isHybrid}
                    className={`w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D] ${
                      isHybrid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="active">Aktiv</option>
                    <option value="pending">Avventer Godkjenning</option>
                    <option value="inactive">Inaktiv</option>
                  </select>
                </div>

                <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-3 text-sm text-kf-blue-800">
                  <p className="font-medium">Aktive kurs: {editingSensor.assigned_courses_count || 0}</p>
                  {editingSensor.organizations && editingSensor.organizations.length > 0 && (
                    <p className="mt-1">
                      <span className="font-medium">Organisasjoner:</span>{' '}
                      {editingSensor.organizations.map(org => org.org_name).join(', ')}
                    </p>
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  {!isHybrid && (
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 bg-[#B5651D] text-white py-2 rounded-lg hover:bg-[#8B4513] transition disabled:opacity-50"
                    >
                      {saving ? 'Lagrer...' : 'Lagre Endringer'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingSensor(null);
                    }}
                    className={`${isHybrid ? 'flex-1' : 'flex-1'} bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition`}
                  >
                    {isHybrid ? 'Lukk' : 'Avbryt'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
